import { useCallback } from "react"

import * as TriageApi from "@/api/useTriage"
import { App } from "@/atoms"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import { toCreateTicketData } from "@/helpers/ticketing.helpers"
import { shallowEqualObjects } from "@/helpers/utils"
import { fetcher, predicateHelpers, useQueryClient } from "@/hooks/useHttp"
import { useLogger } from "@/hooks/useLogger"
import { usePrevious } from "@/hooks/usePrevious"
import { useQueryString } from "@/hooks/useQueryString"
import useServices from "@/hooks/useServices"
import { useTranslation } from "@/hooks/useTranslation"
import { useFeatureFlags } from "@/hooks/useUser"
import {
  overrideDemoIssuePages,
  overrideDemoProjectResponse
} from "@/organisms/Drawers/components/TriageDrawer/components/DemoOverrides/demoOverrides"
import {
  enrichDemoIssuesPages,
  enrichDemoProjectsResponse
} from "@/organisms/Drawers/components/TriageDrawer/components/MitigationDemoCards/enrich"
import {
  actions,
  useSelection
} from "@/pages/TriagePage/application/useSelection"
import {
  getFiltersTriageSelector,
  getIssuesTriageSelectorLoadMore,
  getProjectsTriageSelector,
  projectUpdater
} from "./useTriage.selector"

import type { ProjectsTriageResponse } from "@/api/useTriage.types"
import type { Issue } from "@/domain/issue"
import type { ExploitMaturity } from "@/domain/package"
import type { CreateTicketPayload } from "@/helpers/ticketing.helpers.types"
import type { Params, QueryClient } from "@/hooks/useHttp.types"
import type {
  AllIssuesInProject,
  AttrByFilter,
  Data,
  Issues,
  Payload,
  UseDismissProps
} from "./useTriage.types"

export const getFilterParams = (params: Record<string, unknown>) => {
  const {
    orderBy,
    ascending,
    pageNumber,
    excludeProjectIds,
    excludeIssueIds,
    ...restParams
  } = params

  return restParams
}

export const getArrayFromFilterParams = (param?: unknown) => {
  if (typeof param !== "string") return undefined

  return param.split(",")
}

export const useFiltersTriage = () => {
  const { t } = useTranslation()
  const internetFacingFF = useFeatureFlags("internetFacingIntegration")
  const exploitabilityEnabled = useServices().auth.isDemo()
  const select = useCallback(
    (response: any) =>
      getFiltersTriageSelector(response, {
        t,
        internetFacingFF,
        exploitabilityEnabled
      }),
    [t, internetFacingFF, exploitabilityEnabled]
  )
  const isEnabled = internetFacingFF !== undefined
  const context = TriageApi.useFiltersTriage({ select, enabled: isEnabled })

  return [context, context.isLoading || !isEnabled] as const
}

export const useProjectsTriage = (keys: string[], isEnabled?: boolean) => {
  const params: Record<string, unknown> = useQueryString([
    "orderBy",
    "ascending",
    ...keys
  ])

  const previousParams = usePrevious(params)
  const queryClient = useQueryClient()
  const [selectionState, dispatch] = useSelection()
  const needResetProjectCache =
    selectionState.wasUpdated && !shallowEqualObjects(params, previousParams)

  if (needResetProjectCache) {
    queryClient.removeQueries({ queryKey: [RESOURCES_NAMES.TRIAGE.PROJECTS] })
    dispatch(
      actions.setFlags({ wasUpdated: false, closeAllExpandedParentRows: true })
    )
  }

  const isInternalTenant = !!useFeatureFlags("isInternalTenant")
  const isDemo = useServices().auth.isDemo()
  const orderBy = params.orderBy as string | undefined
  const ascending = params.ascending === "true"
  const select = useCallback(
    (response: Parameters<typeof getProjectsTriageSelector>[0]) =>
      getProjectsTriageSelector(
        overrideDemoProjectResponse(
          enrichDemoProjectsResponse(response, isInternalTenant, {
            orderBy,
            ascending
          }),
          isDemo,
          { orderBy, ascending }
        )
      ),
    [isInternalTenant, isDemo, orderBy, ascending]
  )

  const context = TriageApi.useProjectsTriage(params, {
    select,
    enabled: isEnabled
  })

  return [context, getFilterParams(params)] as const
}

export const useIssuesProjectTriage = (
  params: Params<{ needle: string }>,
  keys: string[],
  config?: any
) => {
  const paramsQueryString = useQueryString(["orderBy", "ascending", ...keys])
  const isInternalTenant = !!useFeatureFlags("isInternalTenant")
  const isDemo = useServices().auth.isDemo()
  const orderBy = paramsQueryString.orderBy as string | undefined
  const ascending = paramsQueryString.ascending === "true"
  const select = useCallback(
    (response: Parameters<typeof getIssuesTriageSelectorLoadMore>[0]) =>
      getIssuesTriageSelectorLoadMore(
        overrideDemoIssuePages(
          enrichDemoIssuesPages(response, isInternalTenant, {
            orderBy,
            ascending
          }),
          isDemo,
          { orderBy, ascending }
        )
      ),
    [isInternalTenant, isDemo, orderBy, ascending]
  )

  const context = TriageApi.useLoadMoreIssuesProjectTriage(
    { ...paramsQueryString, ...params },
    {
      select,
      ...config
    }
  )

  return context
}

export const getOptionsUseDismissIssues =
  (params: Record<string, unknown>, services: ReturnType<typeof useServices>) =>
  (queryClient: QueryClient) => ({
    onSuccess: async (
      _: { projectId: string; needle: string; data: Payload },
      variables: { projectId: string; needle: string; data: Payload }
    ) => {
      const { projectId, needle } = variables

      const result = await queryClient.fetchQuery<ProjectsTriageResponse>({
        staleTime: 0,
        gcTime: 0,
        queryKey: [RESOURCES_NAMES.TRIAGE.PROJECTS, { projectId, ...params }],
        queryFn: ({ queryKey }) => fetcher({ queryKey } as any, services)
      })

      projectUpdater(
        projectId,
        queryClient,
        (project) =>
          getProjectsTriageSelector({
            ...result,
            data: result?.data?.length
              ? result.data
              : [
                  {
                    ...project,
                    kodemScore: 0,
                    relatedApplications: [],
                    issuesSummary: {
                      critical: 0,
                      high: 0,
                      medium: 0,
                      low: 0,
                      negligible: 0,
                      unknown: 0
                    },
                    riskInsights: {
                      runtime: false,
                      internetFacing: "UNDETERMINED" as const,
                      ingress: false,
                      exploitMaturities: ["UNDETERMINED"] as ExploitMaturity[]
                    }
                  }
                ]
          }).data[0]
      )

      const currentIssueUrl = `${RESOURCES_NAMES.TRIAGE.PROJECTS}${needle}`
      const currentIssueRequestKey = [currentIssueUrl, JSON.stringify(params)]
      const currentIssueRequestKeyHash = JSON.stringify(currentIssueRequestKey)

      queryClient.removeQueries({
        queryKey: [currentIssueUrl],
        predicate: ({ queryHash }) =>
          params ? queryHash !== currentIssueRequestKeyHash : false
      })
      queryClient.refetchQueries({ queryKey: currentIssueRequestKey })
      queryClient.refetchQueries({
        predicate: predicateHelpers.includesKeysPredicate([
          RESOURCES_NAMES.TRIAGE.ISSUES_COUNT
        ])
      })
      queryClient.resetQueries({
        predicate: predicateHelpers.includesKeysPredicate(
          [RESOURCES_NAMES.TRIAGE.ISSUES],
          ["/count"]
        )
      })
    },
    onSettled: undefined
  })

export const useDismissIssuesByFilter = (
  attr: AttrByFilter,
  params: Record<string, unknown>
) => {
  const context = TriageApi.useDismissIssuesByFilter<any, unknown>(
    params,
    (queryClient) => ({
      onSettled: async () => {
        return queryClient
          .invalidateQueries({
            predicate: (query) => {
              return (
                predicateHelpers.includesKeysPredicate([
                  RESOURCES_NAMES.TRIAGE.ISSUES
                ])(query) ||
                predicateHelpers.includesKeysPredicate([
                  RESOURCES_NAMES.TRIAGE.PROJECTS
                ])(query) ||
                predicateHelpers.includesKeysPredicate([
                  RESOURCES_NAMES.TRIAGE.ISSUES_COUNT
                ])(query)
              )
            }
          })
          .then(() =>
            queryClient.removeQueries({
              predicate: (query) => {
                if ((query.options as { enabled: boolean }).enabled)
                  return false

                return predicateHelpers.includesKeysPredicate([
                  RESOURCES_NAMES.TRIAGE.PROJECTS
                ])(query)
              }
            })
          )
      }
    })
  )
  const { message } = App.useApp()
  const { t } = useTranslation()
  const [, dispatch] = useSelection()
  const { logger, EVENTS } = useLogger()

  const commonUpdater = async (
    status: "dismissed" | "open",
    reason?: string,
    currentAttr: AttrByFilter = attr,
    currentParams: Record<string, unknown> = params
  ) => {
    try {
      dispatch(actions.setUpdatingAll(true))

      const { totalIssues, resourceId } = currentAttr

      await context.mutateAsync({
        status,
        reason,
        resourceId,
        totalIssues,
        requestFilters: {
          ...getFilterParams(currentParams),
          excludeProjectIds: getArrayFromFilterParams(params.excludeProjectIds),
          excludeIssueIds: getArrayFromFilterParams(params.excludeIssueIds)
        }
      })

      const isDismissed = status === "dismissed"
      const loadingMessageKey = `triage.${
        isDismissed ? "dismiss" : "reopen"
      }_success`

      const { componentName } = attr
      const { DISMISS_ISSUE, REOPEN_ISSUE } = EVENTS.ANALYTIC_EVENTS.TRIAGE
      const meta = { componentName, count: totalIssues }

      logger.info(isDismissed ? DISMISS_ISSUE : REOPEN_ISSUE, meta)

      if (currentAttr?.noMessage) return context

      message.success(
        t(loadingMessageKey, {
          entityType: t("general.issue", meta),
          ...meta
        })
      )

      return context
    } catch (err) {
      logger.error(err as Error)

      if (currentAttr?.noMessage) return

      message.error(t("error.serverGeneralError"))
    } finally {
      dispatch(actions.setUpdatingAll(false))
    }
  }

  const dismissIssuesUpdater = async (reason?: string) =>
    commonUpdater("dismissed", reason)

  const reopenIssuesUpdater = () => commonUpdater("open")

  return { dismissIssuesUpdater, reopenIssuesUpdater, commonUpdater }
}

export const useDismissIssues = (
  params: Record<string, unknown>,
  props: UseDismissProps = {},
  getOptions:
    typeof getOptionsUseDismissIssues | null = getOptionsUseDismissIssues
) => {
  const [, dispatch] = useSelection()
  const services = useServices()
  const { t } = useTranslation()
  const { logger, EVENTS } = useLogger()

  const context = TriageApi.useDismissIssues(
    params,
    getOptions === null ? undefined : getOptions(params, services)
  )
  const contextByFilter = useDismissIssuesByFilter({ totalIssues: 0 }, params)

  const updaterByProject = (projectId: string, data: Data) => {
    try {
      return context.mutateAsync({
        projectId,
        needle: `/${projectId}/issues`,
        data: {
          reason: data.reason,
          status: data.status,
          issueIds: data.issues.map((issue) => `${issue.id}`)
        }
      })
    } catch (err) {
      logger.error(err as Error)
    }
  }

  const updater = async (
    allData: Issues,
    dismiss: boolean,
    attr?: Pick<Payload, "reason">
  ) => {
    let totalUpdatedIssues = undefined
    const { data, projectsAllIssuesData } = Object.keys(allData || {}).reduce(
      (acc, projectId) => {
        if (Array.isArray(allData[projectId]))
          acc.data[projectId] = allData[projectId]
        else acc.projectsAllIssuesData[projectId] = allData[projectId]

        return acc
      },
      {
        data: {} as Record<string, Issue[]>,
        projectsAllIssuesData: {} as Record<string, AllIssuesInProject>
      }
    )

    const projectIds = Object.keys(data || {})
    const loadingMessageKey = `triage.${
      dismiss ? "dismissing" : "reopeningIssue"
    }`
    let withErrorProjectIds: string[] = []
    let successfullyUpdatedProjectIds: string[] = []
    const projectAllIssuesKeys = Object.keys(projectsAllIssuesData)

    if (props.showLoadingMessage) {
      const total = projectIds.reduce((acc, projectId) => {
        acc += data[projectId].length

        return acc
      }, 0)
      props.messageApi?.open({
        key: loadingMessageKey,
        type: "loading",
        content: t(loadingMessageKey, {
          count: total,
          entityType: t("general.issue", { count: total })
        }),
        duration: 0
      })
    }

    try {
      dispatch(actions.setUpdatingStateForParentRow(true, projectIds))

      const status = dismiss ? "dismissed" : "open"
      const reason = dismiss ? attr?.reason : undefined
      const projectsAllIssuesDataCount = Object.values(
        projectsAllIssuesData
      ).reduce((acc, { metadata }) => {
        acc += metadata.issuesTotal

        return acc
      }, 0)

      const [projectResult, issuesResult] = await Promise.all([
        projectAllIssuesKeys.length
          ? contextByFilter.commonUpdater(
              status,
              reason,
              { noMessage: true, totalIssues: projectsAllIssuesDataCount },
              { projectId: projectAllIssuesKeys.join(","), ...params }
            )
          : null,
        Promise.all(
          projectIds.map((projectId) =>
            updaterByProject(projectId, {
              status,
              reason,
              issues: data[projectId]
            })
          )
        )
      ])

      projectIds.forEach((_, index) => {
        if ((issuesResult[index] as { ok: boolean } | undefined)?.ok)
          successfullyUpdatedProjectIds.push(projectIds[index])
        else withErrorProjectIds.push(projectIds[index])
      })

      totalUpdatedIssues = successfullyUpdatedProjectIds.reduce(
        (acc, projectId) => {
          acc += data[projectId].length

          return acc
        },
        0
      )

      const isProjectActionSuccessful = projectResult?.isError === false

      if (isProjectActionSuccessful) {
        totalUpdatedIssues += projectsAllIssuesDataCount
        successfullyUpdatedProjectIds = [
          ...successfullyUpdatedProjectIds,
          ...projectAllIssuesKeys
        ]
      } else {
        withErrorProjectIds = [...withErrorProjectIds, ...projectAllIssuesKeys]
      }

      props.messageApi?.destroy(loadingMessageKey)

      const { componentName } = props
      const meta = { componentName, count: totalUpdatedIssues }

      if (dismiss)
        props.messageApi?.success(
          t("triage.dismiss_success", {
            count: totalUpdatedIssues,
            entityType: t("general.issue", meta)
          })
        )

      props.onSuccess?.()

      const { DISMISS_ISSUE, REOPEN_ISSUE } = EVENTS.ANALYTIC_EVENTS.TRIAGE

      logger.info(dismiss ? DISMISS_ISSUE : REOPEN_ISSUE, meta)
    } catch (err) {
      logger.error(err as Error)
      props.messageApi?.destroy(loadingMessageKey)
      props.messageApi?.error(t("error.serverGeneralError"))

      withErrorProjectIds = projectAllIssuesKeys
    } finally {
      dispatch(
        actions.setUpdatingStateForParentRow(
          false,
          successfullyUpdatedProjectIds,
          withErrorProjectIds
        )
      )
    }
  }

  const dismissIssues = (data: Issues, attr?: Pick<Payload, "reason">) =>
    updater(data, true, attr)
  const reopenIssues = (data: Issues) => updater(data, false)

  return [{ dismissIssues, reopenIssues }, context] as const
}

export const useIssues = () => {
  const { logger, EVENTS } = useLogger()

  const createTicketTriage = TriageApi.useCreateTicketIssue()
  const client = useQueryClient()

  const createTicket = async (
    dataIssue: CreateTicketPayload,
    props?: { id?: string | string[]; parentId?: string }
  ) => {
    if (!props) return

    try {
      let { id: issueId } = props

      if (!issueId) return
      if (typeof issueId === "string") {
        issueId = [issueId]
      }

      const data = toCreateTicketData(dataIssue)

      logger.info(EVENTS.ANALYTIC_EVENTS.TRIAGE.CREATE_ISSUE_JIRA, {
        issueId: issueId.join(","),
        provider: "provider" in data ? data.provider : "jira"
      })

      const result = await createTicketTriage.mutateAsync({
        data: {
          create_ticket_data: { data },
          issues_ids: issueId
        } as any,
        needle: `/ticket`
      })

      await Promise.allSettled(
        client
          .getQueryCache()
          .getAll()
          .filter(
            ({ queryHash }) =>
              issueId.some((id) => queryHash.includes(id)) ||
              queryHash.includes(RESOURCES_NAMES.TRIAGE.PROJECTS)
          )
          .map((q) => client.invalidateQueries(q))
      )

      return result
    } catch (err) {
      return Promise.reject(err)
    }
  }

  return { createTicket, createTicketTriage } as const
}
